import { Component, HostBinding, OnDestroy } from '@angular/core';
import { ModalCheckinService } from '../../modals/modal-checkin/modal-checkin.service';
import { CheckinFilterService } from 'projects/lib-shared-core/src/lib/services/checkin-filter/checkin-filter.service';
import { TranslocoService } from '@ngneat/transloco';
import { EMPTY, Subject, catchError, takeUntil, tap } from 'rxjs';
import { ChecklistAnalyticsService } from 'projects/lib-shared-core/src/lib/services/analytics/checklist/checklist-analytics.service';
import { TasksCategory } from 'projects/lib-shared-core/src/lib/features/main-tasks/models/tasks-category';
import { UserService } from 'projects/lib-shared-core/src/lib/features/user/services/user.service';
import { HttpErrorService } from 'projects/lib-shared-common/src/public-api';

@Component({
  selector: 'svc-location-active',
  templateUrl: './location-active.component.html',
  styleUrls: ['./location-active.component.scss'],
})
export class LocationActiveComponent implements OnDestroy {
  readonly selection = this._checkinFilterService.selection;
  private readonly _destroy$ = new Subject<void>();
  isLoading = true;

  @HostBinding('class.location-active--hidden')
  get hideHost(): boolean {
    return !this.showContainer;
  }

  get showContainer(): boolean {
    return this.isLoading || this.hasSelection;
  }

  private get awaitingLabel(): boolean {
    return this.hasSelection && !this.locationLabel;
  }

  constructor(
    private readonly _modalCheckinService: ModalCheckinService,
    private readonly _translocoService: TranslocoService,
    private readonly _checkinFilterService: CheckinFilterService,
    private readonly _checklistAnalyticsService: ChecklistAnalyticsService,
    private readonly _userService: UserService,
    private readonly _httpErrorService: HttpErrorService
  ) {
    this._checkinFilterService.loadingChanges$
      .pipe(takeUntil(this._destroy$))
      .subscribe((loading) => {
        this._updateLoading(loading);
      });

    this._checkinFilterService.selectionChanges$
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        this._updateLoading(this._checkinFilterService.loading());
      });

    this._updateLoading(this._checkinFilterService.loading());
  }

  get hasSelection(): boolean {
    const selection = this.selection();
    return Boolean(selection?.udaId);
  }

  get locationLabel(): string {
    const selection = this.selection();
    if (!selection?.udaId) {
      return '';
    }

    return selection.udaName?.trim() || '';
  }

  private _updateLoading(serviceLoading: boolean): void {
    const awaitingLabel = this.awaitingLabel;
    this.isLoading = serviceLoading || awaitingLabel;
  }

  openChangeDialog(): void {
    const selection = this.selection();
    this._modalCheckinService.open({
      selectedPlaceId: selection?.udaId ?? undefined,
      selectedEquipmentId: selection?.equipmentId ?? undefined,
    });
  }

  removeSelection(): void {
    const currentUser = this._userService.user;
    if (!currentUser) {
      this._checkinFilterService.clearSelection();
      return;
    }
    const dontAskAgain = false;
    this._userService
      .updateCheckinFilter({
        setAsDefault: false,
        dontAskAgain,
        udaId: null,
        equipmentId: null,
      })
      .pipe(
        tap(() => {
          this._checkinFilterService.clearSelection();
          this._userService.user = {
            ...currentUser,
            checkIn: { setAsDefault: false, udaId: null, equipmentId: null },
            checkInFilter: {
              ...(currentUser.checkInFilter ?? {}),
              dontAskAgain,
              default: null,
              active: null,
              recent: [],
              setAsDefault: false,
            },
            checkinPlaceId: null,
            checkinEquipmentId: null,
            checkinDefault: false,
          };
          this._refreshChecklists();
        }),
        catchError((error) => {
          this._httpErrorService.showErrorInToast(error);
          return EMPTY;
        }),
        takeUntil(this._destroy$)
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  private _refreshChecklists(): void {
    this._checklistAnalyticsService.getInspectionIndicators().pipe(takeUntil(this._destroy$)).subscribe();
    this._checklistAnalyticsService.getInspectionApprovalsIndicator().pipe(takeUntil(this._destroy$)).subscribe();
    this._checklistAnalyticsService.getInspectionCategorizedDrillDown(TasksCategory.TODAY).pipe(takeUntil(this._destroy$)).subscribe();
  }
}




